import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { IconCalendarEvent, IconMapPin, IconUpload } from '@tabler/icons-react';
import { APP_ROUTES } from '../../../navigation/appRoutes';
import {
  SCHEDULER_NAV_PERMISSIONS,
  useCanSchedulerNav,
} from '../../../permissions/scheduler';
import type { ScheduleReadinessState } from '../../schedule-readiness';

const CARD_RADIUS = 'sm';

type SetupPath = {
  title: string;
  description: string;
  to: string;
  label: string;
  icon: React.ReactNode;
  canNavigate: boolean;
};

/**
 * Full-page style empty state when the project has no schedule tasks yet.
 */
export function DashboardScheduleSetupEmpty({ projectLabel }: { projectLabel: string }) {
  const canImport = useCanSchedulerNav(SCHEDULER_NAV_PERMISSIONS.scheduleImport);
  const canTasks = useCanSchedulerNav(SCHEDULER_NAV_PERMISSIONS.scheduleTasks);
  const canLocations = useCanSchedulerNav(SCHEDULER_NAV_PERMISSIONS.scheduleLocations);

  const paths: SetupPath[] = [
    {
      title: 'Import an existing plan',
      description: 'Upload MPP, MSP, P6, or XML and continue into locations.',
      to: APP_ROUTES.schedule.import,
      label: 'Open Project setup',
      icon: <IconUpload size={18} stroke={1.75} />,
      canNavigate: canImport,
    },
    {
      title: 'Start with a blank schedule',
      description: 'Open Tasks and add a root task, then assign locations when ready.',
      to: `${APP_ROUTES.schedule.tasks}?started=blank`,
      label: 'Open Tasks',
      icon: <IconCalendarEvent size={18} stroke={1.75} />,
      canNavigate: canTasks,
    },
    {
      title: 'Build locations first',
      description: 'Set up the site hierarchy, then create or import work.',
      to: APP_ROUTES.schedule.locations,
      label: 'Open Location management',
      icon: <IconMapPin size={18} stroke={1.75} />,
      canNavigate: canLocations,
    },
  ].filter((p) => p.canNavigate);

  return (
    <Card withBorder radius={CARD_RADIUS} p="xl">
      <Stack gap="lg" maw={720} mx="auto" align="stretch">
        <Stack gap={6}>
          <Title order={3}>Get started with {projectLabel}</Title>
          <Text c="dimmed" size="sm">
            There is no schedule yet, so progress and risk metrics have nothing to show. Choose how
            to begin — you can finish linking locations and tasks later.
          </Text>
        </Stack>

        {paths.length === 0 ? (
          <Text size="sm" c="dimmed">
            You do not have permission to set up the schedule. Ask an admin for access to Project
            setup, Tasks, or Location management.
          </Text>
        ) : (
          <Stack gap="md">
            {paths.map((path) => (
              <Group
                key={path.to}
                align="center"
                justify="space-between"
                wrap="nowrap"
                gap="md"
                p="md"
                style={{
                  border: '1px solid var(--mantine-color-default-border)',
                  borderRadius: 10,
                  background: 'var(--mantine-color-body)',
                }}
              >
                <Group align="flex-start" wrap="nowrap" gap="md" style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      flexShrink: 0,
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      border: '1px solid var(--mantine-color-default-border)',
                      background: 'var(--mantine-color-default)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--mantine-color-dimmed)',
                    }}
                    aria-hidden
                  >
                    {path.icon}
                  </div>
                  <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600} size="sm">
                      {path.title}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {path.description}
                    </Text>
                  </Stack>
                </Group>
                <Button component={Link} to={path.to} size="xs" style={{ flexShrink: 0 }}>
                  {path.label}
                </Button>
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

type CalloutProps = {
  state: Extract<ScheduleReadinessState, 'needs_locations' | 'needs_assignment'>;
  unassignedLeafCount: number;
  leafTaskCount: number;
};

/** Inline readiness strip styled like Location management banners. */
export function DashboardScheduleReadinessCallout({
  state,
  unassignedLeafCount,
  leafTaskCount,
}: CalloutProps) {
  const canLocations = useCanSchedulerNav(SCHEDULER_NAV_PERMISSIONS.scheduleLocations);
  const canTasks = useCanSchedulerNav(SCHEDULER_NAV_PERMISSIONS.scheduleTasks);

  const message =
    state === 'needs_locations'
      ? 'Your schedule has tasks, but the location tree is empty or project-only. Build a site structure so work can be placed.'
      : `${unassignedLeafCount} of ${leafTaskCount} leaf task${
          leafTaskCount === 1 ? '' : 's'
        } still need a location. Assign on Tasks or finish linking from Location management.`;

  return (
    <div style={{ borderRadius: 8, overflow: 'hidden' }}>
      <div className="location-mgmt-ai-banner" role="status">
        <div className="location-mgmt-ai-banner-main">
          <span className="location-mgmt-ai-banner-badge">Setup</span>
          <p className="location-mgmt-ai-banner-message" title={message}>
            {message}
          </p>
          {state === 'needs_locations' ? (
            canLocations ? (
              <Button
                className="location-mgmt-ai-banner-cta"
                component={Link}
                to={APP_ROUTES.schedule.locations}
                variant="default"
              >
                Open locations
              </Button>
            ) : null
          ) : (
            <>
              {canTasks ? (
                <Button
                  className="location-mgmt-ai-banner-cta"
                  component={Link}
                  to={APP_ROUTES.schedule.tasks}
                  variant="default"
                >
                  Open Tasks
                </Button>
              ) : null}
              {canLocations ? (
                <Button
                  className="location-mgmt-ai-banner-cta"
                  component={Link}
                  to={APP_ROUTES.schedule.locations}
                  variant="default"
                >
                  Locations
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
